import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { VerticalSpace } from '@brokerbay/design-components';
import { BuildJsonType } from '@bb/domain/dist/typeHelpers';
import { EnhancedAppointment } from '@bb/domain/dist/types';
import type { FeedbackTemplate } from '@bb/domain/dist/types/Feedback';

import errorReporter from '@app/errorReporter';
import { AppExternalController, FeedbackController } from 'api';
import {
  Button,
  Checkbox,
  Empty,
  Icon,
  InputTextArea,
  message,
  Radio,
  RadioGroup,
  Select,
  SelectOption,
  Space,
  Text,
  theme,
  Tooltip,
} from 'components';
import { featureEnabled } from 'utils';
import { FEEDBACK_TYPES } from '@/constants';
import { FeedbackForCoop } from 'api/FeedbackController/getFeedbackForCoop';
import { ShowingDetailsPageUserType } from '../types';

import Section from './Section';

export const showFeedbackSection = (
  appointment: BuildJsonType<EnhancedAppointment>,
) => {
  const { type, status, passed } = appointment;
  const isAcceptingFeedback = FEEDBACK_TYPES.includes(type);
  const isConfirmedAndInPast = status === 'confirm' && passed;
  return isAcceptingFeedback && isConfirmedAndInPast;
};

type FeedbackSectionProps = {
  auth: { id?: string };
  dashboardId?: string;
  userType: ShowingDetailsPageUserType;
  appointment: BuildJsonType<EnhancedAppointment>;
  feedbackConfig: FeedbackForCoop;
  fetchFeedback: () => Promise<void>;
  fetchAppointment: () => Promise<void>;
  setFeedbackConfig: React.Dispatch<React.SetStateAction<FeedbackForCoop>>;
};

const FeedbackSection = ({
  auth,
  appointment,
  dashboardId,
  userType,
  feedbackConfig,
  fetchFeedback,
  fetchAppointment,
  setFeedbackConfig,
}: FeedbackSectionProps) => {
  const canEdit = userType !== 'client';
  const [editing, setEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  useEffect(() => {
    setShowFeedback(Boolean(appointment.feedbackCollected));
  }, [appointment, userType, dashboardId]);

  const someFeedbackAnswered = useMemo(
    () => feedbackConfig?.template.some(template => !!template.response),
    [feedbackConfig],
  );

  if (!feedbackConfig) return <></>;

  const onInputChange = (e: any, label: string) => {
    const updatedTemplate = feedbackConfig.template.map(temp => {
      if (temp.label === label) {
        let updatedResponse: string;
        switch (temp.type) {
          case 'select': {
            updatedResponse = e;
            break;
          }
          case 'checkbox': {
            updatedResponse = e.join('  ');
            break;
          }
          default: {
            updatedResponse = e.target?.value;
            break;
          }
        }

        return {
          ...temp,
          response: updatedResponse,
        };
      }
      return temp;
    });

    setFeedbackConfig({ ...feedbackConfig, template: updatedTemplate });
  };

  const buildFeedbackInput = (input: FeedbackTemplate) => {
    switch (input.type) {
      case 'radio':
        return (
          <>
            <FeedbackLabel>{input.label}</FeedbackLabel>
            <RadioGroup
              dataCy="feedback-radio-group"
              value={input.response}
              disabled={!editing}
              onChange={e => onInputChange(e, input.label)}
            >
              <Space direction="vertical">
                {input.options.map((option, index) => (
                  <Radio value={option} key={`radio-${option}-${index}`}>
                    {option}
                  </Radio>
                ))}
              </Space>
            </RadioGroup>
          </>
        );

      case 'select':
        return (
          <>
            <FeedbackLabel>{input.label}</FeedbackLabel>
            <Select
              dataCy="feedback-select"
              disabled={!editing}
              value={input.response}
              onChange={e => onInputChange(e, input.label)}
            >
              {input.options.map((option, index) => (
                <SelectOption key={`select-${option}-${index}`} value={option}>
                  {option}
                </SelectOption>
              ))}
            </Select>
          </>
        );

      case 'checkbox':
        return input.options.length > 0 ? (
          <>
            <FeedbackLabel>{input.label}</FeedbackLabel>
            <Checkbox.Group
              data-cy="feedback-checkbox-group"
              options={input.options}
              value={input.response?.split('  ')}
              disabled={!editing}
              onChange={e => onInputChange(e, input.label)}
            />
          </>
        ) : (
          <></>
        );

      default:
        return (
          <>
            <FeedbackLabel>{input.label}</FeedbackLabel>
            {editing ? (
              <InputTextArea
                dataCy="feedback-input-text-area"
                value={input.response}
                onChange={e => onInputChange(e, input.label)}
                imposeWrap
              />
            ) : (
              <Text dataCy="feedback-input-text">{input.response}</Text>
            )}
          </>
        );
    }
  };

  const EmptyFeedback: React.FC<{ canSubmit: boolean }> = ({ canSubmit }) => (
    <Empty description="Feedback has not been recorded yet.">
      {canSubmit && (
        <Button
          onClick={() => {
            setEditing(true);
            setShowFeedback(true);
          }}
        >
          Submit Feedback
        </Button>
      )}
    </Empty>
  );

  const saveFeedback = async () => {
    setIsSubmitting(true);
    try {
      if (dashboardId) {
        await AppExternalController.submitFeedback({
          dashboardId,
          appointmentId: appointment.id,
          feedback: {
            id: feedbackConfig.id,
            template: feedbackConfig.template,
          },
        });
      } else if (userType === 'coop') {
        await FeedbackController.saveFeedbackByCoop({
          listingId: appointment.listingId,
          appointmentId: appointment.id,
          feedback: {
            ...feedbackConfig,
            appointment: appointment.id,
            listing: appointment.listingId,
            contactId: appointment.coop.id,
            template: [...feedbackConfig.template],
            coopSubmitted: true,
          },
        });
      } else if (userType === 'agent' && auth?.id) {
        await FeedbackController.saveFeedback({
          appointmentId: appointment.id,
          listingId: appointment.listingId,
          feedback: {
            ...feedbackConfig,
            appointment: appointment.id,
            listing: appointment.listingId,
            contactId: auth.id,
            template: [...feedbackConfig.template],
          },
        });
      }

      await fetchFeedback();
      await fetchAppointment();
      setEditing(false);
      message.success('Feedback has been saved');
    } catch (err) {
      errorReporter.error(err);
      message.error('Failed to save the feedback');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Section
        id="feedback-section"
        title="Feedback"
        headingIcon={
          canEdit && showFeedback ? (
            <Button
              type="default"
              shape="circle"
              onClick={() => setEditing(!editing)}
              data-cy="feedback-edit-button"
            >
              <Icon type="edit" />
            </Button>
          ) : (
            <></>
          )
        }
      >
        {showFeedback ? (
          <>
            <p>
              We would really appreciate your client&apos;s feedback. Please
              fill in the form below and add any additional comments you may
              have. Thank you in advance!
            </p>
            <VerticalSpace size="small">
              {feedbackConfig.template.map((input, index) => (
                <React.Fragment
                  key={`feedback-${input.type}-${input.label}-${index}`}
                >
                  {buildFeedbackInput(input)}
                </React.Fragment>
              ))}
            </VerticalSpace>
            {editing && (
              <Footer>
                <Tooltip
                  placement="left"
                  title={
                    !someFeedbackAnswered
                      ? 'Please answer at least one feedback question'
                      : ''
                  }
                >
                  <div>
                    <Button
                      type="primary"
                      onClick={saveFeedback}
                      data-cy="feedback-submit-button"
                      disabled={!someFeedbackAnswered || isSubmitting}
                    >
                      Submit
                    </Button>
                  </div>
                </Tooltip>
              </Footer>
            )}
          </>
        ) : (
          <EmptyFeedback canSubmit={userType !== 'client'} />
        )}
      </Section>
    </>
  );
};

export default FeedbackSection;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid ${theme.gray4};
  padding-top: 10px;
  margin-top: 20px;
`;

const FeedbackLabel = styled(Text)`
  margin-bottom: 8px;
`;
